import { jsx, jsxs } from "react/jsx-runtime";
import { Carousel, CdnIcon, Heading, Highlight, MediaSnippet, P, Span, Stage } from "@pcln/horizon";
import { useCallback, useMemo } from "react";
import { heroCollectionVariants } from "./HeroCollection.variants.js";
const { root, headingBlock, headingRow, desktopLink, mobileLink } = heroCollectionVariants();
function CollectionLink({ link, className, onSelect }) {
    return /*#__PURE__*/ jsxs("a", {
        href: link.url,
        target: link.openInNewTab ? '_blank' : '_self',
        rel: link.openInNewTab ? 'noopener noreferrer' : void 0,
        "aria-label": link.ariaLabel,
        className: className,
        onClick: ()=>onSelect(link.analyticsTracking),
        children: [
            /*#__PURE__*/ jsx(Span, {
                textStyle: "body2",
                bold: true,
                palette: "actionSecondary",
                shade: "12",
                children: link.label
            }),
            /*#__PURE__*/ jsx(CdnIcon, {
                iconName: "arrow_forward",
                size: "20",
                palette: "actionSecondary",
                shade: "12",
                "aria-hidden": "true"
            })
        ]
    });
}
function buildSnippetProps(item, handleSelect) {
    const base = {
        ratio: '1/1',
        alt: item.image?.altText ?? '',
        imageUrl: item.image?.asset?.file?.url,
        heading: item.heading,
        body: item.paragraph,
        caption: item.overlayText,
        showAd: item.showAdHighlight,
        ariaLabel: item.link?.ariaLabel
    };
    if (item.link) return {
        ...base,
        role: 'link',
        href: item.link.url,
        onClick: ()=>handleSelect(item.link?.analyticsTracking),
        anchorProps: item.link.openInNewTab ? {
            target: '_blank',
            rel: 'noopener noreferrer'
        } : void 0
    };
    return {
        ...base,
        role: 'button'
    };
}
function buildCarouselSlides(collection, handleSelect) {
    return collection.map((item, index)=>({
            id: `hero-snippet-${index}`,
            childrenNodes: /*#__PURE__*/ jsx(MediaSnippet, {
                ...buildSnippetProps(item, handleSelect)
            })
        }));
}
function HeroCollection({ heroCollection, handleSelect }) {
    const { collectionHeading, collectionLink, collectionSubheading, heroImage, heroHeading, heroBody, heroLink, collection, showAdHighlight } = heroCollection;
    const onSelect = useCallback((analyticsTracking)=>handleSelect(analyticsTracking), [
        handleSelect
    ]);
    const stageHeader = useMemo(()=>{
        if (!heroHeading && !heroBody && !showAdHighlight && !heroLink) return;
        return /*#__PURE__*/ jsxs("div", {
            className: "flex items-end justify-between gap-4",
            children: [
                /*#__PURE__*/ jsxs("div", {
                    className: "flex flex-col gap-1",
                    children: [
                        heroHeading && /*#__PURE__*/ jsx(Heading, {
                            as: "h3",
                            palette: "primary",
                            shade: "1",
                            className: "text-heading3 @md:text-heading2",
                            children: heroHeading
                        }),
                        /*#__PURE__*/ jsxs("div", {
                            className: "flex flex-col items-start gap-2 @md:flex-row @md:items-center @md:gap-6",
                            children: [
                                heroBody && /*#__PURE__*/ jsx(P, {
                                    textStyle: "body2",
                                    palette: "neutral",
                                    shade: "1",
                                    className: "text-body2 @md:text-body1",
                                    textWrap: "pretty",
                                    children: heroBody
                                }),
                                heroLink?.url && heroLink.label && /*#__PURE__*/ jsxs("div", {
                                    className: "flex items-center gap-2",
                                    children: [
                                        /*#__PURE__*/ jsx(Span, {
                                            textStyle: "body2",
                                            bold: true,
                                            palette: "primary",
                                            shade: "1",
                                            children: heroLink.label
                                        }),
                                        /*#__PURE__*/ jsx(CdnIcon, {
                                            iconName: "arrow_forward",
                                            size: "20",
                                            palette: "primary",
                                            shade: "1",
                                            "aria-hidden": "true"
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                showAdHighlight && /*#__PURE__*/ jsx(Highlight, {
                    palette: "neutral",
                    textStyle: "disclaimer",
                    children: "Ad"
                })
            ]
        });
    }, [
        heroHeading,
        heroBody,
        showAdHighlight,
        heroLink
    ]);
    const slides = useMemo(()=>buildCarouselSlides(collection, onSelect), [
        collection,
        onSelect
    ]);
    return /*#__PURE__*/ jsxs("div", {
        className: root(),
        "data-testid": "hero-collection",
        children: [
            collectionHeading && /*#__PURE__*/ jsxs("div", {
                className: headingBlock(),
                children: [
                    /*#__PURE__*/ jsxs("div", {
                        className: headingRow(),
                        children: [
                            /*#__PURE__*/ jsx(Heading, {
                                as: "h2",
                                children: collectionHeading
                            }),
                            collectionLink && /*#__PURE__*/ jsx(CollectionLink, {
                                link: collectionLink,
                                className: desktopLink(),
                                onSelect: onSelect
                            })
                        ]
                    }),
                    collectionSubheading && /*#__PURE__*/ jsx(Span, {
                        textStyle: "body1",
                        palette: "neutral",
                        shade: "13",
                        children: collectionSubheading
                    })
                ]
            }),
            heroImage && /*#__PURE__*/ jsx(Stage, {
                media: true,
                image: {
                    contentfulUrl: heroImage.asset.file.url,
                    alt: heroImage.altText
                },
                imageRole: heroLink?.url ? 'link' : void 0,
                imageHref: heroLink?.url,
                onImageClick: heroLink?.url ? ()=>onSelect(heroLink.analyticsTracking) : void 0,
                imageAriaLabel: heroLink?.ariaLabel ?? heroHeading,
                imageAnchorProps: heroLink?.openInNewTab ? {
                    target: '_blank',
                    rel: 'noopener noreferrer'
                } : void 0,
                header: stageHeader,
                body: /*#__PURE__*/ jsx(Carousel, {
                    type: "content",
                    slideWidth: "same",
                    numSlides: "two-three-four-five-six-seven",
                    pagination: "numbers",
                    bleed: "customBleed",
                    bleedSpacing: "3",
                    ariaLabel: collectionHeading ?? 'Hero collection',
                    slides: slides
                })
            }),
            !heroImage && slides.length > 0 && /*#__PURE__*/ jsx(Carousel, {
                type: "content",
                slideWidth: "same",
                numSlides: "two-three-four-five-six-seven",
                pagination: "numbers",
                bleed: "customBleed",
                bleedSpacing: "3",
                ariaLabel: collectionHeading ?? 'Hero collection',
                slides: slides
            }),
            collectionLink && /*#__PURE__*/ jsx(CollectionLink, {
                link: collectionLink,
                className: mobileLink(),
                onSelect: onSelect
            })
        ]
    });
}
export { HeroCollection };
